import { useEffect, useState } from "react";
import { RiThumbDownFill, RiThumbUpFill } from "react-icons/ri";
import { calcUtcDatetime } from "../../../../utils/basis_util";
import { useTranslation } from "react-i18next";
import { useErrorDialog, useEvaluateUsageLog } from "../../../../hooks";

interface EvaluateManualProps {
  userId: string;
  displayPage: string;
  reference: string;
}

// マニュアルのGood/Bad評価を制御する
export const EvaluateManual: React.FC<EvaluateManualProps> = ({
  userId,
  displayPage,
  reference,
}) => {
  //言語切替用
  const { t } = useTranslation();
  //type EvalType = "Good" | "Bad" | ""; //マニュアル評価はGood/Bad/なしの3種類
  const [evalManual, setEvalManual] = useState<string>(""); // マニュアル評価
  useEffect(() => {
    console.log("evalManual: ", evalManual);
  }, [evalManual]);
  // Good・Bad登録処理中かどうか
  const [isRegistering, setIsRegistering] = useState<boolean>(false);

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  // Good・Bad評価の利用ログ登録
  const { handleEvaluationUsageLog } = useEvaluateUsageLog();

  const handleEvaluation = async (evaluation: string) => {
    // Good・Bad登録処理中の場合は何もしない
    if (isRegistering) return;
    // Good・Bad登録処理開始
    setIsRegistering(true);
    try {
      if (!userId) {
        throw new Error("userId is unknown");
      }
      if (!reference) {
        throw new Error("reference is unknown");
      }
      const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)
      const additionalInfo = evaluation; //引数の評価を設定
      let isRegister = false; // 登録が成功したか失敗したか
      console.log("handleEvaluation start.");
      //Good/Bad評価の登録
      if (evalManual === "") {
        //未評価の場合、Good/Badの評価はそのまま登録して終了
        isRegister = await registerEvaluation(
          userId,
          displayPage,
          displayDateTime,
          additionalInfo + "," + reference
        );
        if (isRegister) {
          //正常に登録出来た場合、マニュアル評価情報更新
          setEvalManual(evaluation);
        } else {
          throw new Error("failed to register evaluation");
        }
      } else {
        //評価済みの場合、同一評価の場合は取り消し、異なる評価への変更は取り消し+新評価で登録
        if (evalManual === evaluation) {
          isRegister = await handleCancelEvaluation(
            userId,
            displayPage,
            displayDateTime
          );
          if (isRegister) {
            //正常に登録出来た場合、マニュアル評価情報更新
            setEvalManual("");
          }
          if (!isRegister) {
            throw new Error("failed to cancel evaluation");
          }
        } else {
          const procState = await handleCancelAndReEvaluation(
            userId,
            displayPage,
            displayDateTime
          );
          if (procState === "Complete") {
            //正常に登録出来た場合、マニュアル評価情報更新
            setEvalManual(evaluation);
          } else if (procState === "Cancel Complete") {
            //キャンセルまで成功した場合、評価なしでマニュアル評価情報更新
            setEvalManual("");
          }
          if (procState !== "Complete") {
            throw new Error("failed to cancel and reEvaluation");
          }
        }
      }
    } catch (err) {
      console.log("[How To Fix]handleEvaluation err :", err);
      if (!userId || !reference) {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "closeTab"
        ); //エラー", "通信エラーが発生しました。(タブを閉じる)
      } else if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "closeTab"
        ); //エラー", "セッションがタイムアウトしました。(タブを閉じる)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。")
        ); //エラー", "通信エラーが発生しました。
      }
    } finally {
      // Good・Bad登録完了
      setIsRegistering(false);
    }
  };

  const handleCancelAndReEvaluation = async (
    userId: string,
    displayPage: string,
    displayDateTime: string
  ) => {
    try {
      let isRegister = false;
      let procState = "Failed"; //Failed/Cancel Complete/Complete
      isRegister = await handleCancelEvaluation(
        userId,
        displayPage,
        displayDateTime
      );
      if (isRegister) {
        procState = "Cancel Complete";
        if (evalManual === "Good") {
          //Good->Badへの変更
          if (isRegister) {
            isRegister = await registerEvaluation(
              userId,
              displayPage,
              displayDateTime,
              "Bad," + reference
            );
          }
        } else {
          //Bad->Goodへの変更
          if (isRegister) {
            isRegister = await registerEvaluation(
              userId,
              displayPage,
              displayDateTime,
              "Good," + reference
            );
          }
        }
        if (isRegister) {
          procState = "Complete";
        }
      }
      return procState;
    } catch (err) {
      throw err;
    }
  };

  //評価取り消しを登録する
  const handleCancelEvaluation = async (
    userId: string,
    displayPage: string,
    displayDateTime: string
  ) => {
    try {
      let isRegister = false;
      if (evalManual === "Good") {
        //Goodの取り消しを登録する
        isRegister = await registerEvaluation(
          userId,
          displayPage,
          displayDateTime,
          "Cancel Good," + reference
        );
      } else {
        //Badの取り消しを登録する
        isRegister = await registerEvaluation(
          userId,
          displayPage,
          displayDateTime,
          "Cancel Bad," + reference
        );
      }
      return isRegister;
    } catch (err) {
      throw err;
    }
  };

  //評価を登録する
  const registerEvaluation = async (
    userId: string,
    displayPage: string,
    displayDateTime: string,
    additionalInfo: string
  ) => {
    try {
      //利用ログ登録(Good/Badの登録)
      const evaluationUsageLogResult = await handleEvaluationUsageLog({
        userId,
        displayPage,
        displayDateTime,
        additionalInfo,
      });

      return evaluationUsageLogResult;
    } catch (err) {
      throw err;
    }
  };

  return (
    <div className="flex justify-around">
      {/* Goodボタン */}
      <button
        onClick={() => handleEvaluation("Good")}
        className="p-2"
        disabled={isRegistering}
      >
        <RiThumbUpFill
          fill="none"
          strokeWidth={2}
          className={
            evalManual === "Good"
              ? "fill-blue-300 stroke-sub-blue size-[30px] "
              : "stroke-sub-blue size-[30px] "
          }
        />
      </button>
      {/* Badボタン */}
      <button
        onClick={() => handleEvaluation("Bad")}
        className="p-2"
        disabled={isRegistering}
      >
        <RiThumbDownFill
          fill="none"
          strokeWidth={2}
          className={
            evalManual === "Bad"
              ? "fill-red-400 stroke-main-red size-[30px] "
              : "stroke-main-red size-[30px] "
          }
        />
      </button>
    </div>
  );
};
